/**
 * Modos A y C de activación (#79) y registro de excepciones (#80).
 *
 * Los tipos de la parte compartida salen del OpenAPI generado
 * (`ActivationResultOut`, `ActivationRejectOut`); lo propio de los endpoints
 * nuevos se declara acá **a mano y a propósito**. El motivo: `npm run api:types`
 * regenera `src/api/schema.d.ts` entero desde la API levantada, y con tres
 * agentes agregando recursos en paralelo eso arrastra endpoints de otros
 * issues a medio terminar. Cuando B10 cierre, se corre el generador una vez y
 * este archivo se queda sólo con las funciones puras.
 *
 * La lógica de este módulo es pura y está separada de la pantalla porque es la
 * parte que se puede equivocar en silencio: un tramo mal parseado anula
 * etiquetas que no correspondían, y VOIDED no tiene vuelta atrás.
 */
import type { components } from '@/api/schema';
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

type BaseResult = components['schemas']['ActivationResultOut'];

// ───────────────────────────────────────────────────── contratos de la API

/** Motivos de descarte. Lista cerrada: el motivo se agrega y se compara. */
export const VOID_REASONS = [
  'damaged',
  'misapplied',
  'skipped',
  'print_discard',
  'order_cancelled',
  'other',
] as const;

export type VoidReason = (typeof VOID_REASONS)[number];

export interface SerialRange {
  serial_from: number;
  serial_to: number;
}

/**
 * Declaración de excepciones del modo A. **Obligatoria**: activar un rango sin
 * declararlas deja unidades activadas sin producto real, que autentican como
 * genuinas y no corresponden a nada.
 *
 * `{ none: true }` no es el valor por defecto: es la firma de un operario
 * diciendo "este rollo se aplicó entero".
 */
export type ExceptionSpec =
  | { none: true }
  | {
      reason: VoidReason;
      note?: string | null;
      codes?: string[];
      ranges?: SerialRange[];
    };

export interface RangeBody {
  client_batch_id: string;
  batch_id: number;
  serial_from: number;
  serial_to: number;
  exceptions: ExceptionSpec;
  line_id?: string | null;
  client_sent_at?: string | null;
}

export interface RangeResult extends BaseResult {
  mode: 'range';
  serial_from: number;
  serial_to: number;
  roll_id: number | null;
  /** Unidades del rango que existen en la base. */
  in_range: number;
  exception_id: number;
  /** Cuántas se anularon ANTES de activar el rango. */
  voided: number;
}

export interface UnitBody {
  batch_id: number;
  code?: string;
  codes?: string[];
  line_id?: string | null;
}

export interface UnitOutcome {
  code: string;
  outcome: string;
  existing_batch_id: number | null;
  existing_status: number | null;
}

export interface UnitResult {
  mode: 'unit';
  batch_id: number;
  requested: number;
  activated: number;
  already: number;
  rejected: number;
  outcomes: Record<string, number>;
  units: UnitOutcome[];
  cache_invalidated: boolean;
  elapsed_ms: number;
}

export interface ExceptionBody {
  reason: VoidReason;
  note?: string | null;
  batch_id?: number | null;
  codes?: string[];
  serial_from?: number;
  serial_to?: number;
  client_ref?: string | null;
}

export interface ExceptionRecord {
  id: number;
  batch_id: number | null;
  scope: 'codes' | 'range' | 'none';
  reason: string;
  note: string | null;
  serial_from: number | null;
  serial_to: number | null;
  client_ref: string | null;
  requested: number;
  voided: number;
  rejected: number;
  outcomes: Record<string, number>;
  duration_ms: number | null;
  created_by: number | null;
  created_at: string;
}

export interface VoidReject {
  unit_id: number;
  code: string | null;
  outcome: string;
  existing_batch_id: number | null;
  existing_status: number | null;
}

export interface ExceptionResult {
  exception: ExceptionRecord;
  replayed: boolean;
  outcomes: Record<string, number>;
  rejects: VoidReject[];
  cache_invalidated: boolean;
  elapsed_ms: number;
}

// ─────────────────────────────────────────────── vocabulario de la pantalla

const REASON_LABELS: Record<VoidReason, MessageKey> = {
  damaged: 'activation.reason.damaged',
  misapplied: 'activation.reason.misapplied',
  skipped: 'activation.reason.skipped',
  print_discard: 'activation.reason.printDiscard',
  order_cancelled: 'activation.reason.orderCancelled',
  other: 'activation.reason.other',
};

export function reasonLabelKey(reason: VoidReason): MessageKey {
  return REASON_LABELS[reason];
}

/**
 * Desenlaces de una anulación. Verde no existe a propósito: anular no es un
 * logro, es una pérdida registrada. `activated` va en rojo porque es el caso
 * que exige que alguien mire — hay producto real con esa etiqueta.
 */
const VOID_TONES: Record<string, Tone> = {
  voided: 'neutral',
  already_voided: 'neutral',
  activated: 'critical',
  terminal: 'suspect',
  wrong_state: 'suspect',
  unknown: 'critical',
  invalid_code: 'critical',
  duplicate: 'neutral',
};

const VOID_LABELS: Record<string, MessageKey> = {
  voided: 'activation.voidOutcome.voided',
  already_voided: 'activation.voidOutcome.alreadyVoided',
  activated: 'activation.voidOutcome.activated',
  terminal: 'activation.voidOutcome.terminal',
  wrong_state: 'activation.voidOutcome.wrongState',
  unknown: 'activation.voidOutcome.unknown',
  invalid_code: 'activation.voidOutcome.invalidCode',
  duplicate: 'activation.outcome.duplicate',
};

export function voidOutcomeTone(outcome: string): Tone {
  return VOID_TONES[outcome] ?? 'neutral';
}

export function voidOutcomeLabelKey(outcome: string): MessageKey | null {
  return VOID_LABELS[outcome] ?? null;
}

// ────────────────────────────────────────────────────────── funciones puras

export interface ParsedRanges {
  ranges: SerialRange[];
  /** Renglones que no son un tramo. No se envían y se muestran. */
  invalid: string[];
  /** Cuántas unidades cubren los tramos válidos. */
  units: number;
}

/**
 * Texto → tramos de series. Acepta `1000-1010`, `1000..1010` y `1000` (una
 * sola unidad), uno por renglón o separados por coma.
 *
 * Un renglón que no se entiende **no se ignora en silencio**: vuelve en
 * `invalid` para que la pantalla lo muestre. Anular por un tramo mal leído no
 * tiene deshacer.
 */
export function parseRanges(input: string): ParsedRanges {
  const ranges: SerialRange[] = [];
  const invalid: string[] = [];
  let units = 0;

  for (const raw of input.split(/[\n,;]+/)) {
    const token = raw.trim();
    if (token.length === 0) continue;
    const match = /^(\d+)(?:\s*(?:-|\.\.)\s*(\d+))?$/.exec(token);
    const inicio = match?.[1];
    if (inicio === undefined) {
      invalid.push(token);
      continue;
    }
    const fin = match?.[2];
    const from = Number.parseInt(inicio, 10);
    const to = fin === undefined ? from : Number.parseInt(fin, 10);
    if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || to < from) {
      invalid.push(token);
      continue;
    }
    ranges.push({ serial_from: from, serial_to: to });
    units += to - from + 1;
  }

  return { ranges, invalid, units };
}

/** Cuántas unidades abarca `[from, to]`. `0` si el rango no tiene sentido. */
export function rangeWidth(from: number | null, to: number | null): number {
  if (from === null || to === null || to < from) return 0;
  return to - from + 1;
}

/**
 * ¿Todos los tramos caen dentro del rango que se va a activar?
 *
 * La API lo vuelve a validar (`exception_outside_range`), pero decirlo antes
 * de enviar evita que un cero de más anule etiquetas de otro rollo.
 */
export function outsideRange(
  ranges: readonly SerialRange[],
  from: number,
  to: number,
): SerialRange[] {
  return ranges.filter((r) => r.serial_from < from || r.serial_to > to);
}

/** p50 de las latencias medidas en el cliente, para el panel del modo C. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const alto = sorted[middle] ?? 0;
  if (sorted.length % 2 !== 0) return alto;
  return ((sorted[middle - 1] ?? alto) + alto) / 2;
}
